import { useMemo } from 'react'

import { uniq } from 'lodash'
import { ExternalLink } from 'lucide-react'

import { SplitsLink } from '~/components/Link'
import { LabelValue, NetworkDisplay } from '~/components/component-library'
import { CostDisplay } from '~/components/txn-display/cost-display/CostDisplay'
import { SponsoredFeeAmount } from '~/components/txn-display/cost-display/SponsoredFeeAmount'
import {
  FeeAmount,
  getFeeAmount,
  sumFeeAmounts,
} from '~/components/txn-display/cost-display/feeAmount'
import { useGasPaidByLabel } from '~/hooks/transactions/useGasPaidByLabel'
import { useTransactionCost } from '~/hooks/transactions/useTransactionCost'
import {
  OrgMerkleTransaction,
  OrgRegularTransaction,
  PublicMerkleTransaction,
  PublicRegularTransaction,
} from '~/types'
import { getExplorerTransactionLinkChainId } from '~/utils/addresses'
import { cn } from '~/utils/tailwind-utils'

export const MerkleTransactionCostDisplay = ({
  transaction,
}: {
  transaction: OrgMerkleTransaction | PublicMerkleTransaction
}) => {
  // Only self-paid children need the gas-field estimate. The hook refetches
  // whenever this array changes identity, so it is memoized.
  const selfPaidChildren = useMemo(
    () =>
      transaction.childTransactions.filter(
        (childTransaction) => !childTransaction.isSponsored,
      ) as OrgRegularTransaction[],
    [transaction.childTransactions],
  )
  const transactionCost = useTransactionCost({
    isMerkleTransaction: true,
    childTransactions: selfPaidChildren,
  })

  // Each chain has at most one child, so the estimate is keyed by chain.
  const childFees = transaction.childTransactions.map((childTransaction) =>
    getFeeAmount({
      isSponsored: childTransaction.isSponsored,
      sponsoredGasCost: childTransaction.sponsoredGasCost,
      estimateUsd:
        transactionCost.transactionCostByChain?.[childTransaction.chainId],
    }),
  )

  const areAllChildTransactionsSponsored = transaction.childTransactions.every(
    (childTransaction) => childTransaction.isSponsored,
  )

  // A mixed transaction's summary is what the account itself pays.
  const selfPaidFees = childFees.filter(
    (_, index) => !transaction.childTransactions[index].isSponsored,
  )
  const summaryFee = sumFeeAmounts(
    areAllChildTransactionsSponsored ? childFees : selfPaidFees,
  )

  // One payer across every chain goes on the summary row; otherwise each
  // chain row spells out its own.
  const payers = uniq(
    transaction.childTransactions.map((child) => child.gasPaidBy),
  )
  const hasOnePayer = payers.length === 1
  const paidBy = useGasPaidByLabel({
    gasPaidBy: hasOnePayer ? payers[0] : undefined,
    smartAccountId: transaction.smartAccountId,
  })

  return (
    <CostDisplay
      isCollapsible
      defaultOpen={false}
      isLoading={transactionCost.isLoading}
      isSponsored={areAllChildTransactionsSponsored}
      paidBy={paidBy}
      fee={summaryFee}
    >
      <div
        className={cn(
          'relative flex flex-col gap-2.5 rounded-lg bg-white px-3.5 py-3 text-sm text-secondary ring-1 ring-inset ring-gray-200 dark:bg-gray-900 dark:ring-gray-700',
        )}
      >
        {transaction.childTransactions.map((childTransaction, index) => {
          const explorerLink = getExplorerTransactionLinkChainId(
            childTransaction.transactionHash,
            childTransaction.chainId,
          )

          const feeBadge = (
            <ChildTransactionFee
              transaction={childTransaction}
              fee={childFees[index]}
              hideBadgeText={hasOnePayer}
            />
          )

          return (
            <LabelValue
              key={index}
              label={
                <NetworkDisplay
                  layout="standard"
                  size="small"
                  chainId={childTransaction.chainId}
                />
              }
              size="small"
              value={
                explorerLink ? (
                  <SplitsLink
                    className="flex flex-shrink-0 items-center gap-2"
                    path={explorerLink}
                    target="tab"
                  >
                    {feeBadge}
                    {explorerLink && <ExternalLink className="size-3.5" />}
                  </SplitsLink>
                ) : (
                  <span className="flex flex-shrink-0 items-center gap-2">
                    {feeBadge}
                  </span>
                )
              }
            />
          )
        })}
      </div>
    </CostDisplay>
  )
}

const ChildTransactionFee = ({
  transaction,
  fee,
  hideBadgeText,
}: {
  transaction: OrgRegularTransaction | PublicRegularTransaction
  fee: FeeAmount
  hideBadgeText: boolean
}) => {
  const paidBy = useGasPaidByLabel(transaction)
  return (
    <SponsoredFeeAmount
      fee={fee}
      isSponsored={transaction.isSponsored}
      paidBy={paidBy}
      hideBadgeText={hideBadgeText}
    />
  )
}
